<template>
	<section ref="el" data-component="team">
		<div class="g-row">
			<div class="xxl-8 xxl-offset-3 sm-12 sm-offset-2 xs-16 xs-offset-0 title-wrapper">
				<!-- <p class="-title-4">{{ title }}</p> -->
				<h3 class="-title-4 title">
					<SanityContent :value="title" :components="components" />
				</h3>
			</div>
		</div>
		<div class="g-row">
			<div class="xxl-4 xxl-offset-6 sm-12 sm-offset-5 xs-16 xs-offset-1 description-wrapper">
				<svg width="48" height="151" viewBox="0 0 48 151" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M0.750062 -2.01072e-06L0.750044 14.6176C0.750021 32.6985 9.27328 49.7214 23.75 60.5539C38.2266 71.3863 46.7499 88.4092 46.7499 106.49L46.75 151"
						stroke="#0551F5"
						stroke-width="1.5"
					/>
				</svg>

				<p class="-body-regular description">{{ description }}</p>
			</div>
		</div>

		<div v-if="isMobile" class="team-cards-mobile">
			<div ref="scrollContainerRef" class="team-cards-scroll">
				<div class="inner">
					<CardTeam v-for="(member, i) in members" :key="i" v-bind="member" />
				</div>
			</div>
			<div ref="scrollbarRef" class="custom-scrollbar" @mousedown="handleScrollbarClick" @touchstart.prevent="handleScrollbarClick">
				<div ref="scrollThumbRef" class="scroll-thumb" />
			</div>
		</div>

		<div v-else class="g-row team-cards">
			<CardTeam v-for="(member, i) in members" :key="i" data-parallax :data-friction="setFriction(i)" v-bind="member" />
		</div>
	</section>
</template>

<script setup>
import { h } from 'vue'
import { gsap, ScrollTrigger } from 'gsap/all'

import { CardTeam } from '~/components/fragments'

import { useScreen } from 'vue-screen'
import { useClientOnlyComputed, useHorizontalScroll } from '~/composables/core'

import { breakpoints } from '~/config'

import.meta.client && gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
	title: { type: [Array, Boolean], default: undefined },
	description: { type: String, default: undefined },
	members: { type: Array, default: undefined },
})

const components = {
	block: {
		normal: (_, { slots }) => h('span', { class: 'title-line' }, slots.default?.()),
	},
}

const el = ref(null)
const context = inject('pageContext')
const screen = useScreen()
const scrollContainerRef = ref(null)
const scrollbarRef = ref(null)
const scrollThumbRef = ref(null)

const isMobile = useClientOnlyComputed(false, () => screen.width <= breakpoints.xsmall, screen)

const { handleScrollbarClick } = useHorizontalScroll({
	container: scrollContainerRef,
	scrollbar: scrollbarRef,
	thumb: scrollThumbRef,
	enabled: isMobile,
	dragEaseDuration: 0.22,
	inertiaMultiplier: 520,
	inertiaMaxDistance: 1100,
	inertiaMinVelocity: 0.06,
})

const setFriction = (i) => {
	const membersLength = props.members?.length || 0

	return (i + 1 === membersLength && membersLength % 2 === 0) || i % 3 === 0 || (i - 2) % 3 === 0 ? 1 : -1
}

let parallaxContext = null
let canInitParallax = false

const setup = () => {}
const init = () => {
	if (!import.meta.client || !el.value || isMobile.value) return

	parallaxContext = gsap.context(() => {
		el.value.querySelectorAll('[data-parallax]').forEach((card) => {
			const friction = Number(card.dataset.friction) || 1

			gsap.fromTo(
				card,
				{
					yPercent: 10 * friction,
				},
				{
					yPercent: -10 * friction,
					ease: 'none',
					scrollTrigger: {
						trigger: card,
						scrub: true,
					},
				}
			)
		})
	}, el.value)
}

const kill = () => {
	if (!import.meta.client) return

	parallaxContext?.revert()
	parallaxContext = null

	const parallaxCards = el.value?.querySelectorAll('[data-parallax]')
	if (!parallaxCards?.length) return

	gsap.killTweensOf(parallaxCards)
	gsap.set(parallaxCards, { clearProps: 'transform' })
}

watch(
	[isMobile, () => props.members?.length],
	async () => {
		if (!canInitParallax) return

		kill()
		await nextTick()
		init()
	}
)

onBeforeUnmount(() => kill())
onMounted(() => {
	const loader = context?.$page?.loader

	if (!loader) {
		setup()
		canInitParallax = true
		init()
		return
	}

	loader.loaded.then(() => setup())
	loader.transitionReady.then(() => {
		canInitParallax = true
		init()
	})
})
</script>

<style lang="scss" scoped>
[data-component='team'] {
	position: relative;

	width: 100vw;
	padding-top: rs(100, 100);
	margin-top: rs(30, 30);
	padding-bottom: rs(420, 100);

	background: linear-gradient(180deg, var(--color-river-blue-50) 80%, rgba(255, 255, 255, 0) 100%);

	overflow: hidden;

	@include max-width($sm) {
		padding-bottom: rs(420, 200);
	}

	@include max-width($xs) {
		padding-bottom: 0;
	}

	.title-wrapper {
	}

	.title {
		white-space: pre-line;
	}

	.title :deep(.title-line) {
		display: block;
		@include inherit-font;

		color: var(--color-neutral-tints-900);

		&:first-child {
			text-indent: column-width(2, true);
		}
	}

	.title :deep(strong) {
		color: var(--color-river-blue-500);
		@include inherit-font;
	}

	.description-wrapper {
		position: relative;

		padding-top: rs(150, 135);
		margin-top: rs(24, 24);

		svg {
			height: auto;
			display: flex;

			position: absolute;
			top: 0;
			left: 0;
			width: rs(48, 48);
		}
		.description {
			position: relative;

			padding-left: rs(70, 60);

			color: var(--color-neutral-tints-700);

			@include max-width($sm) {
				width: column-width(8);
			}

			@include max-width($xs) {
				width: column-width(14);
			}

			&:before {
				content: '';
				position: absolute;
				left: rs(46, 46);
				height: 100%;
				width: 1.5px;
				background-color: var(--color-river-blue-500);
			}
		}
	}

	.team-cards-mobile {
		margin-top: rs(-120, 48);
		padding-bottom: rs(100, 106);

		position: relative;
	}

	.team-cards-scroll {
		position: relative;
		z-index: 1;

		overflow-x: auto;
		overflow-y: hidden;
		touch-action: pan-y;
		-ms-overflow-style: none;
		scrollbar-width: none;
		padding-top: rs(16, 16);
		padding-bottom: rs(150, 146);

		&::-webkit-scrollbar {
			display: none;
		}

		.inner {
			display: flex;
			flex-wrap: nowrap;
			gap: rs(16, 16);

			width: max-content;
			padding: 0 var(--grid-padding);

			> * {
				flex: 0 0 auto;
			}
		}
	}

	.custom-scrollbar {
		position: relative;
		z-index: 3;

		width: column-width(12);
		height: rs(3, 2);
		background-color: rgb(from var(--color-river-blue-500) r g b / 0.14);
		border-radius: rs(12);
		margin: rs(-110, -80) auto 0;

		@include extend_hitbox(10);

		.scroll-thumb {
			position: absolute;
			left: 0;
			height: 100%;
			width: rs(50);
			background-color: var(--color-river-blue-500);
			border-radius: 5px;
			cursor: pointer;
			touch-action: none;

			@include extend_hitbox(20);
		}
	}

	.team-cards {
		margin-top: rs(-120, 48);

		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		column-gap: rs(85, 85);
		row-gap: rs(112, 112);
		justify-items: center;
		align-items: start;

		width: calc(100% - column-width(2) - var(--grid-gap));

		> :nth-child(1) {
			grid-column: 1;
			grid-row: 1;
		}

		> :nth-child(2) {
			grid-column: 2;
			grid-row: 1;
			transform: translateY(50%);
		}

		> :nth-child(3) {
			grid-column: 3;
			grid-row: 1;
		}

		> :nth-child(4) {
			grid-column: 1;
			grid-row: 2;
		}

		> :nth-child(5) {
			grid-column: 3;
			grid-row: 2;
		}

		:nth-child(6) {
			grid-column: 2;
			grid-row: 2;
			transform: translateY(50%);
		}
	}
}
</style>
